/**
 * ISO 4217 货币小数位（minor unit exponent）—— 框架通道原生价格的 minor units ↔ 主单位换算专用。
 *
 * 为什么不用 `~/lib/format` 的 Intl 小数位：`Intl.NumberFormat` 的 `maximumFractionDigits` 是 CLDR 的**展示**
 * 小数位，与 ISO 4217 不同（HUF / IDR / PKR / COP 等 CLDR 为 0、ISO 为 2；IQD CLDR 为 0、ISO 为 3）。C 端把
 * `payment_channel_prices.amount_minor_units` 按 ISO 4217 换算后发给 PayerMax（`02-API契约/65-三方支付通道框架.md`
 * §7.2），运营若按 CLDR 口径录入，成交金额差一个数量级即错账。
 *
 * 与 C 端 `novel-app-server` `src/lib/iso4217-minor-units.ts` 的 0 / 3 / 4 位表逐字同表；改动须两边一起改。
 * 可定价币种（字典 65 §9）中未列出的为 2 位；非可定价币种（非现行码、XAU / XTS / XXX 等小数位为 N.A. 的码）
 * 没有小数位定义，返回 null —— 不按 2 位猜测换算（与 C 端同名函数同口径）。
 */
import { isPriceableCurrency } from '@novel-admin/shared';

/** ISO 4217 小数位为 0 的币种。 */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'ISK',
  'JPY',
  'KMF',
  'KRW',
  'PYG',
  'RWF',
  'UGX',
  'UYI',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

/** ISO 4217 小数位为 3 的币种。 */
const THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BHD',
  'IQD',
  'JOD',
  'KWD',
  'LYD',
  'OMR',
  'TND',
]);

/** ISO 4217 小数位为 4 的币种（记账单位）。 */
const FOUR_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set(['CLF', 'UYW']);

/** 可定价币种的 ISO 4217 小数位；非可定价币种返回 null（无小数位定义，调用方据此不换算）。 */
export function iso4217MinorUnitExponent(currency: string): number | null {
  if (!isPriceableCurrency(currency)) return null;
  if (ZERO_DECIMAL_CURRENCIES.has(currency)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(currency)) return 3;
  if (FOUR_DECIMAL_CURRENCIES.has(currency)) return 4;
  return 2;
}
